import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, InputGroup, Table } from 'react-bootstrap';
import Select from 'react-select';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { get_typology_cases, get_typology_stats } from '../../../utils/services';
import HelpdeskDateRange, { getLast3MonthsRange } from './HelpdeskDateRange';
import MiniStatusBar from './MiniStatusBar';
import Modal_proyectos from '../../menus/Modal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const NONE_KEY = 'none';
const NONE_LABEL = 'Sin tipología';
const TOP_N_DEFAULT = 8;
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const SERIES_COLORS = [
  'rgba(54, 162, 235, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(75, 192, 192, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(255, 205, 86, 1)',
  'rgba(201, 203, 207, 1)',
  'rgba(40, 167, 69, 1)',
  'rgba(220, 53, 69, 1)',
  'rgba(13, 110, 253, 1)',
  'rgba(111, 66, 193, 1)',
  'rgba(253, 126, 20, 1)',
];

function parseMySQLDateTime(mysqlString) {
  if (!mysqlString) return null;
  const [fecha, hora] = String(mysqlString).split(' ');
  const [ano, mes, dia] = fecha.split('-').map(Number);
  const timeParts = (hora || '00:00:00').split(':').map(Number);
  const [horas = 0, minutos = 0, segundos = 0] = timeParts;
  return new Date(ano, mes - 1, dia, horas, minutos, segundos);
}

function getElapsedHours(openfrom, dateLast) {
  if (dateLast == null) dateLast = openfrom;
  const fechaInicial = parseMySQLDateTime(dateLast);
  if (!fechaInicial || isNaN(fechaInicial.getTime())) return 0;
  const fechaActual = new Date();
  return parseInt((fechaActual.getTime() - fechaInicial.getTime()) / (1000 * 60 * 60), 10);
}

function getHoursBetweenDates(dateOpen, dateClose) {
  if (!dateOpen || !dateClose) return 0;
  const d1 = parseMySQLDateTime(dateOpen);
  const d2 = parseMySQLDateTime(dateClose);
  if (!d1 || !d2 || isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
  return Math.floor((d2.getTime() - d1.getTime()) / (1000 * 60 * 60));
}

function getStatusText(status) {
  switch (parseInt(status, 10)) {
    case 0:
      return 'Cerrado';
    case 1:
      return 'Abierto';
    case 2:
      return 'En observación';
    default:
      return '-';
  }
}

function getAgeCellStyle(agehours) {
  if (typeof agehours !== 'number' || agehours < 0) {
    return { backgroundColor: 'transparent' };
  }
  if (agehours >= 60) return { backgroundColor: 'red', color: 'white' };
  if (agehours >= 48) return { backgroundColor: 'orange' };
  if (agehours >= 24) return { backgroundColor: 'yellow' };
  return { backgroundColor: 'transparent' };
}

function toYmd(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function typologyKeyFromAgg(row) {
  if (row.type_id == null || row.type_id === '') return NONE_KEY;
  return String(row.type_id);
}

function typologyLabelFromAgg(row) {
  if (typologyKeyFromAgg(row) === NONE_KEY) return NONE_LABEL;
  return row.typology || NONE_LABEL;
}

function mapIssueRow(element) {
  const serial = element.serial_num == null ? '' : String(element.serial_num);
  const rawAgehours = element.agehours;
  const agehoursVal =
    rawAgehours != null && rawAgehours !== '' && !isNaN(Number(rawAgehours))
      ? Number(rawAgehours)
      : element.last_update
        ? getHoursBetweenDates(element.date, element.last_update)
        : getElapsedHours(element.date, element.date);
  return {
    id: element.id,
    email: serial === '' ? element.email : serial,
    openfrom: element.date,
    CAS_id: Number(element.CAS_id || 0),
    CAS_status: element.CAS_status,
    lastInteraction: element.last_update,
    agehours: agehoursVal,
    status: parseInt(element.status, 10),
    source: serial === '' ? 2 : 1,
  };
}

function CasesTypologyStats({ token }) {
  const navigate = useNavigate();
  const [totals, setTotals] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [uniqueCasesInScope, setUniqueCasesInScope] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [casOnly, setCasOnly] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const { startDate, endDate } = getLast3MonthsRange();
    return [{ startDate, endDate, key: 'selection' }];
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedTypologies, setSelectedTypologies] = useState([]);
  const [detailTypology, setDetailTypology] = useState(null);
  const [detailCases, setDetailCases] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const datePickerRef = useRef(null);

  const filterPayload = useMemo(() => {
    const filters = { cas_only: casOnly };
    if (dateRange[0]?.startDate && dateRange[0]?.endDate) {
      filters.date_start = toYmd(dateRange[0].startDate);
      filters.date_end = toYmd(dateRange[0].endDate);
    }
    return filters;
  }, [casOnly, dateRange]);

  useEffect(() => {
    async function fetchStats() {
      setIsLoading(true);
      setTotals([]);
      setMonthly([]);
      setUniqueCasesInScope(0);
      try {
        const payload = await get_typology_stats(token, filterPayload);
        if (payload === 'INVALID') {
          navigate('/');
          return;
        }
        setTotals(Array.isArray(payload?.totals) ? payload.totals : []);
        setMonthly(Array.isArray(payload?.monthly) ? payload.monthly : []);
        setUniqueCasesInScope(Number(payload?.unique_cases) || 0);
      } catch (err) {
        console.error('Error fetching typology stats:', err);
        setTotals([]);
        setMonthly([]);
        setUniqueCasesInScope(0);
      } finally {
        setIsLoading(false);
      }
    }
    if (token) fetchStats();
  }, [token, navigate, filterPayload]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setIsDatePickerOpen(false);
      }
    }
    if (isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);

  const rankingAll = useMemo(() => {
    return totals
      .map((row) => ({
        key: typologyKeyFromAgg(row),
        label: typologyLabelFromAgg(row),
        count: Number(row.count) || 0,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [totals]);

  const typologyOptions = useMemo(
    () => rankingAll.map((item) => ({ value: item.key, label: `${item.label} (${item.count})` })),
    [rankingAll]
  );

  useEffect(() => {
    if (isLoading) return;
    const top = rankingAll.slice(0, TOP_N_DEFAULT).map((item) => ({
      value: item.key,
      label: `${item.label} (${item.count})`,
    }));
    setSelectedTypologies(top);
  }, [casOnly, dateRange, isLoading, totals, rankingAll]);

  const selectedKeys = useMemo(() => {
    if (!selectedTypologies || selectedTypologies.length === 0) {
      return new Set(rankingAll.map((r) => r.key));
    }
    return new Set(selectedTypologies.map((o) => o.value));
  }, [selectedTypologies, rankingAll]);

  const rankingFiltered = useMemo(
    () => rankingAll.filter((item) => selectedKeys.has(item.key)),
    [rankingAll, selectedKeys]
  );

  const totalFilteredAssignments = useMemo(
    () => rankingFiltered.reduce((sum, item) => sum + item.count, 0),
    [rankingFiltered]
  );

  const barChartData = useMemo(() => {
    const labels = rankingFiltered.map((r) => r.label);
    const values = rankingFiltered.map((r) => r.count);
    return {
      labels,
      datasets: [
        {
          label: 'Casos',
          data: values,
          backgroundColor: 'rgba(13, 110, 253, 0.75)',
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [rankingFiltered]);

  const lineChartData = useMemo(() => {
    const byTypologyMonth = new Map();
    const monthSet = new Set();

    monthly.forEach((row) => {
      const key = typologyKeyFromAgg(row);
      if (!selectedKeys.has(key)) return;
      const monthKey = row.month;
      if (!monthKey) return;
      monthSet.add(monthKey);
      if (!byTypologyMonth.has(key)) byTypologyMonth.set(key, new Map());
      byTypologyMonth.get(key).set(monthKey, Number(row.count) || 0);
    });

    const sortedMonths = Array.from(monthSet).sort();
    const labels = sortedMonths.map((k) => {
      const [y, m] = k.split('-');
      return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
    });

    const datasets = rankingFiltered.map((item, index) => {
      const monthMap = byTypologyMonth.get(item.key) || new Map();
      const color = SERIES_COLORS[index % SERIES_COLORS.length];
      return {
        label: item.label,
        data: sortedMonths.map((m) => monthMap.get(m) || 0),
        borderColor: color,
        backgroundColor: color.replace(', 1)', ', 0.12)'),
        tension: 0.3,
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 5,
      };
    });

    return { labels, datasets };
  }, [monthly, selectedKeys, rankingFiltered]);

  const handleDateChange = useCallback((item) => {
    if (item.selection) setDateRange([item.selection]);
  }, []);

  const clearDateFilter = useCallback((e) => {
    e?.stopPropagation?.();
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setIsDatePickerOpen(false);
  }, []);

  const handleTypologyChange = useCallback((opts) => {
    setSelectedTypologies(opts || []);
  }, []);

  const closeTypologyDetail = useCallback(() => {
    setDetailTypology(null);
    setDetailCases([]);
    setDetailLoading(false);
  }, []);

  const openTypologyDetail = useCallback(
    async (item) => {
      setDetailTypology({ key: item.key, label: item.label });
      setDetailCases([]);
      setDetailLoading(true);
      try {
        const payload = await get_typology_cases(token, {
          ...filterPayload,
          type_id: item.key,
        });
        if (payload === 'INVALID') {
          navigate('/');
          return;
        }
        const cases = Array.isArray(payload?.cases) ? payload.cases.map(mapIssueRow) : [];
        setDetailCases(cases);
      } catch (err) {
        console.error('Error fetching typology cases:', err);
        setDetailCases([]);
      } finally {
        setDetailLoading(false);
      }
    },
    [token, filterPayload, navigate]
  );

  const openIssue = useCallback((row) => {
    let targetUrl;
    if (row.source === 1) {
      targetUrl = `/helpdesk?serial=${row.email}`;
    } else if (row.source === 2) {
      targetUrl = `/helpdesk?email=${row.email}`;
    }
    if (targetUrl) window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, []);

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted mb-0">Cargando estadísticas de tipologías...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        <div className="btn-group" role="group" aria-label="Filtro CAS">
          <Button
            variant={!casOnly ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setCasOnly(false)}
          >
            Todos los casos
          </Button>
          <Button
            variant={casOnly ? 'info' : 'outline-info'}
            size="sm"
            onClick={() => setCasOnly(true)}
          >
            Solo remitidos a CAS
          </Button>
        </div>

        <div className="position-relative" ref={datePickerRef}>
          <InputGroup onClick={() => setIsDatePickerOpen((v) => !v)} style={{ cursor: 'pointer', minWidth: '220px' }}>
            <Form.Control
              readOnly
              value={
                dateRange[0].startDate && dateRange[0].endDate
                  ? `${dateRange[0].startDate.toLocaleDateString('es-ES')} - ${dateRange[0].endDate.toLocaleDateString('es-ES')}`
                  : 'Rango de fechas...'
              }
              className="toolbar-control"
              style={{
                cursor: 'pointer',
                borderTopRightRadius: dateRange[0].startDate ? 0 : '0.375rem',
                borderBottomRightRadius: dateRange[0].startDate ? 0 : '0.375rem',
              }}
            />
            {dateRange[0].startDate && (
              <Button
                variant="light"
                onClick={clearDateFilter}
                className="d-flex align-items-center border"
                style={{ zIndex: 5 }}
              >
                <i className="bi bi-x-circle-fill" style={{ color: 'grey', fontSize: '1rem' }} />
              </Button>
            )}
          </InputGroup>
          {isDatePickerOpen && (
            <div className="position-absolute shadow-lg bg-white" style={{ zIndex: 1050, top: '100%', left: 0, marginTop: 4 }}>
              <HelpdeskDateRange
                range={dateRange}
                handleDateChange={handleDateChange}
                setIsDatePickerOpen={setIsDatePickerOpen}
              />
            </div>
          )}
        </div>

        <div style={{ minWidth: 280, flex: '1 1 320px' }}>
          <Select
            isMulti
            options={typologyOptions}
            value={selectedTypologies}
            onChange={handleTypologyChange}
            placeholder="Filtrar tipologías..."
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => 'Sin tipologías'}
            closeMenuOnSelect={false}
          />
        </div>
      </div>

      <p className="mb-3 text-muted">
        Casos en alcance: <strong>{uniqueCasesInScope}</strong>
        {' · '}
        Asignaciones de tipología (filtro): <strong>{totalFilteredAssignments}</strong>
        {casOnly ? ' · Solo remitidos a CAS' : ' · Todos los casos'}
      </p>

      {rankingFiltered.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded bg-light">
          No hay datos de tipologías para los filtros seleccionados.
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-lg-5">
              <div className="p-3 border rounded bg-light h-100">
                <h6 className="mb-3 fw-bold">Casos por tipología</h6>
                <div style={{ height: Math.max(280, rankingFiltered.length * 28) }}>
                  <Bar
                    data={barChartData}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => ` ${ctx.parsed.x} casos`,
                          },
                        },
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          title: { display: true, text: 'Casos' },
                          ticks: { precision: 0 },
                        },
                        y: {
                          ticks: { autoSkip: false },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="p-3 border rounded bg-light h-100">
                <h6 className="mb-3 fw-bold">Evolución mensual por tipología</h6>
                <div style={{ height: 320 }}>
                  {lineChartData.labels.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      Sin datos temporales
                    </div>
                  ) : (
                    <Line
                      data={lineChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                            },
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: { display: true, text: 'Casos' },
                            ticks: { precision: 0 },
                          },
                          x: {
                            title: { display: true, text: 'Mes' },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border rounded">
            <Table hover responsive className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Tipología</th>
                  <th className="text-end">Casos</th>
                  <th className="text-end">% del total filtrado</th>
                </tr>
              </thead>
              <tbody>
                {rankingFiltered.map((item, idx) => {
                  const pct =
                    totalFilteredAssignments > 0
                      ? Math.round((item.count / totalFilteredAssignments) * 1000) / 10
                      : 0;
                  return (
                    <tr
                      key={item.key}
                      role="button"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openTypologyDetail(item)}
                      title="Ver casos de esta tipología"
                    >
                      <td>{idx + 1}</td>
                      <td>{item.label}</td>
                      <td className="text-end">{item.count}</td>
                      <td className="text-end">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal_proyectos
        isOpen={!!detailTypology}
        onClose={closeTypologyDetail}
        title={detailTypology ? `Casos: ${detailTypology.label}` : 'Casos'}
        headerExtra={
          detailTypology ? (
            <span className="text-muted small">
              {detailLoading
                ? 'Cargando...'
                : `${detailCases.length} caso${detailCases.length === 1 ? '' : 's'}`}
              {casOnly ? ' · Solo remitidos a CAS' : ''}
            </span>
          ) : null
        }
      >
        <Table hover responsive className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Email del caso</th>
              <th>Apertura</th>
              <th>CAS status</th>
              <th>Horas</th>
              <th>Horas totales</th>
              <th>Tipo</th>
            </tr>
          </thead>
          <tbody>
            {detailLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                  Cargando casos...
                </td>
              </tr>
            ) : detailCases.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-muted">
                  No hay casos para esta tipología con los filtros actuales.
                </td>
              </tr>
            ) : (
              detailCases.map((row) => (
                <tr key={row.id} onClick={() => openIssue(row)} style={{ cursor: 'pointer' }}>
                  <td>{row.email}</td>
                  <td>{row.openfrom}</td>
                  <td>
                    {Number(row.CAS_id) > 0 && parseInt(row.CAS_status, 10) === 0 && (
                      <span className="badge bg-secondary">Remitido</span>
                    )}
                    {parseInt(row.CAS_status, 10) > 0 && (
                      <MiniStatusBar currentStep={parseInt(row.CAS_status, 10)} totalSteps={8} />
                    )}
                  </td>
                  <td style={getAgeCellStyle(row.agehours)}>{row.agehours}</td>
                  <td>{getElapsedHours(row.openfrom, row.openfrom)}</td>
                  <td>
                    <span
                      className={`badge ${
                        row.status === 0 ? 'bg-success' : row.status === 1 ? 'bg-danger' : 'bg-primary'
                      }`}
                    >
                      {getStatusText(row.status)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Modal_proyectos>
    </div>
  );
}

export default CasesTypologyStats;
